import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, mediaUrl, CATEGORIES } from "../../lib/api";
import { Search, ArrowUpRight } from "lucide-react";
import ProductModal from "./ProductModal";

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [cat, setCat] = useState("All Products");
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (cat !== "All Products") params.category = cat;
      if (search) params.search = search;
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [cat, search]);

  return (
    <section id="catalog" className="relative py-24 lg:py-36 bg-ink-2" data-testid="catalog">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-px bg-signal" />
              <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-signal">Product Catalog</span>
            </div>
            <h2 className="font-head font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white">
              The complete<br /><span className="text-signal">motion stack.</span>
            </h2>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search components, brands…" data-testid="catalog-search"
              className="w-full bg-ink border border-white/10 pl-11 pr-4 py-3 font-mono text-sm text-white placeholder:text-slate-600 focus:border-signal focus:outline-none transition-colors"
            />
          </div>
        </div>

        {/* filters */}
        <div className="flex flex-wrap gap-2 mb-12">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
              className={`font-mono text-[11px] uppercase tracking-wider px-4 py-2 border transition-colors ${
                cat === c ? "bg-signal text-ink border-signal" : "border-white/10 text-slate-400 hover:border-signal/50 hover:text-white"
              }`}>
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 font-mono text-sm text-slate-500" data-testid="catalog-loading">Loading components…</div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 font-mono text-sm text-slate-500" data-testid="catalog-empty">No components match your filter.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalog-grid">
            {products.map((p, i) => (
              <motion.button
                key={p.id} onClick={() => setActive(p)} data-testid={`product-card-${p.slug}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group text-left bg-card border border-white/10 hover:border-signal/50 transition-colors overflow-hidden ticks"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-ink">
                  <img src={mediaUrl(p.image_url)} alt={p.name}
                    className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                  <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-ink/80 text-signal px-2 py-1 border border-signal/30">
                    {p.brand}
                  </span>
                  <span className="absolute top-3 right-3 w-9 h-9 grid place-items-center bg-signal text-ink opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all">
                    <ArrowUpRight className="w-5 h-5" />
                  </span>
                </div>
                <div className="p-6">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">{p.category}</div>
                  <h3 className="font-head font-bold uppercase text-xl text-white leading-tight mb-3">{p.name}</h3>
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4">{p.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(p.specs || {}).slice(0, 2).map(([k, v]) => (
                      <span key={k} className="font-mono text-[10px] text-slate-300 border border-white/10 px-2 py-1">
                        {k}: <span className="text-signal">{v}</span>
                      </span>
                    ))}
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <ProductModal product={active} onClose={() => setActive(null)}
        onEnquire={() => { setActive(null); document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth" }); }} />
    </section>
  );
}
